/**
 * Shared read/write-phased scroll coordinator.
 *
 * Several handlers on this theme each throttled themselves into their own
 * requestAnimationFrame and each read geometry before writing style, which is
 * the correct discipline inside one callback and buys nothing across several.
 * Three independent callbacks land in the same frame, so the first one's writes
 * invalidate layout for the second and the second's for the third, and each one
 * pays a full synchronous layout. Measured on the live homepage under a 6x CPU
 * throttle: header.js 503-528ms, engagement-signals 473-508ms, the back-to-top
 * sundial 412-433ms, all three already "fixed" individually.
 *
 * This runs one frame for all of them: shared geometry is read once, then every
 * subscriber's read() runs while layout is still clean, then every subscriber's
 * write() runs. No read can follow a write, because the phases are separated for
 * the whole page rather than per handler.
 *
 * Subscribe with an object, not a callback:
 *
 *   const stop = window.CoraScroll.subscribe({
 *     read(m) { return m.scrollY > 180; },   // no style writes in here
 *     write(showing) { el.classList.toggle('is-visible', showing); },
 *   });
 *
 * read() receives the shared metrics and returns whatever write() needs. A
 * subscriber may read its own extra geometry inside read() - layout is clean
 * there - but a write in the read phase reintroduces exactly the defect this
 * exists to remove.
 *
 * stats() always returns { frames, subscribers, names }. With DEBUG on
 * (?coraScrollDebug in the URL, or window.__CORA_SCROLL_DEBUG = true before
 * this file loads) it also returns a `timings` array of { name, read, write,
 * total } per subscriber, sorted by total cost descending, and the live
 * subscriber count is mirrored onto document.documentElement.dataset.
 * coraScrollSubs so it is visible in a DOM snapshot without console access.
 * A subscriber's own extra reads inside read() are exactly what this
 * attributes - otherwise a bad subscriber's cost is invisible, buried inside
 * the coordinator's own frame time.
 */

(function () {
  'use strict';

  // This asset is referenced by layout/theme.liquid and, independently, by
  // snippets/sundial-scroll-top.liquid, which also renders inside 15 standalone
  // article layouts that bypass theme.liquid. On a page carrying both tags the
  // browser executes both, and a second install would replace the live API,
  // orphaning every subscriber the first one had already adopted and running
  // two frames per scroll. Installing once is the whole point of the file.
  if (window.CoraScroll && window.CoraScroll.__installed) {
    return;
  }

  // The stub collects subscribers that registered before this file loaded.
  // header.js is injected via requestIdleCallback and the sundial is inline, so
  // document order guarantees nothing about who runs first.
  const pending = (window.CoraScroll && window.CoraScroll.q) || [];

  // If the stub's degraded path activated before this file ran, it left an
  // unbatched per-event scroll listener attached. Installing on top of it would
  // run both, which is worse than either alone. The stub's gate makes that
  // near-impossible now, but the stub is inline in rendered HTML and this asset
  // is cached separately, so the two can be different vintages on one page.
  const stopFallback = window.CoraScroll && window.CoraScroll.__fallbackStop;

  // Debug instrumentation is opt-in and gated behind a single boolean everywhere
  // it is checked, so the off-cost is one comparison per phase. Without it, a
  // subscriber that reads its own extra geometry inside read() (permitted above)
  // has no way to be attributed - its cost lands inside the coordinator's frame
  // and looks like "the coordinator got slower" with no profiler on the device.
  const DEBUG =
    /coraScrollDebug/.test((window.location && window.location.search) || '') ||
    window.__CORA_SCROLL_DEBUG === true;
  const timings = DEBUG ? new Map() : null; // name -> { read: ms, write: ms }

  function now() {
    return window.performance && window.performance.now ? window.performance.now() : Date.now();
  }

  function recordTiming(name, phase, ms) {
    let t = timings.get(name);
    if (!t) {
      t = { read: 0, write: 0 };
      timings.set(name, t);
    }
    t[phase] += ms;
  }

  function reflectSubCount() {
    if (DEBUG && document.documentElement) {
      document.documentElement.dataset.coraScrollSubs = String(subscribers.size);
    }
  }

  const subscribers = new Set();
  let frameRequested = false;
  let frames = 0;

  // scrollHeight is the expensive one, so it is measured only when the document
  // has actually changed size rather than on every frame. Lazy sections and
  // images landing are what change it, and a ResizeObserver on documentElement
  // catches both without measuring in the callback itself.
  let docDirty = true;
  let scrollHeight = 0;

  const metrics = {
    scrollY: 0,
    innerWidth: 0,
    innerHeight: 0,
    scrollHeight: 0,
    maxScroll: 1,
  };

  function readShared() {
    metrics.scrollY = window.scrollY;
    metrics.innerWidth = window.innerWidth;
    metrics.innerHeight = window.innerHeight;

    if (docDirty) {
      docDirty = false;
      scrollHeight = document.documentElement.scrollHeight;
    }
    metrics.scrollHeight = scrollHeight;
    metrics.maxScroll = Math.max(1, scrollHeight - metrics.innerHeight);
  }

  function frame() {
    frameRequested = false;
    frames++;

    readShared();

    // Read phase. Layout is clean here: nothing has written since the browser
    // committed the previous frame.
    const results = [];
    subscribers.forEach((sub) => {
      let value;
      const t0 = DEBUG ? now() : 0;
      try {
        value = sub.read ? sub.read(metrics) : undefined;
      } catch (err) {
        value = undefined;
        reportError(sub, 'read', err);
      }
      if (DEBUG) {
        recordTiming(sub.name || '(anonymous)', 'read', now() - t0);
      }
      results.push([sub, value]);
    });

    // Write eligibility is frozen here, once, right after the read phase and
    // before any write runs. A subscriber that unsubscribed itself (or was
    // unsubscribed) during the read phase is correctly excluded - it is not
    // in `subscribers` by the time this filter runs. But once a write is
    // queued to run this frame, a later subscriber's write() unsubscribing
    // someone else must not retroactively cancel that someone's write in the
    // frame already in progress; that removal only takes effect starting the
    // next frame, when this filter runs again against the mutated Set.
    const toWrite = results.filter((entry) => subscribers.has(entry[0]));

    // Write phase. Every read is already done, so these writes invalidate
    // layout for nobody until the next frame's reads, which happen after the
    // browser has committed.
    for (let i = 0; i < toWrite.length; i++) {
      const sub = toWrite[i][0];
      if (!sub.write) {
        continue;
      }
      const t0 = DEBUG ? now() : 0;
      try {
        sub.write(toWrite[i][1], metrics);
      } catch (err) {
        reportError(sub, 'write', err);
      }
      if (DEBUG) {
        recordTiming(sub.name || '(anonymous)', 'write', now() - t0);
      }
    }
  }

  // One misbehaving subscriber must not take the scroll path down for the rest
  // of the page, so a throwing phase is isolated and reported rather than
  // allowed to abort the loop.
  function reportError(sub, phase, err) {
    if (window.console && window.console.error) {
      window.console.error(
        '[CoraScroll] subscriber ' + phase + ' failed:',
        sub.name || '(anonymous)',
        err
      );
    }
  }

  function schedule() {
    if (frameRequested || subscribers.size === 0) {
      return;
    }
    frameRequested = true;
    requestAnimationFrame(frame);
  }

  function subscribe(sub) {
    if (!sub || (typeof sub.read !== 'function' && typeof sub.write !== 'function')) {
      return function () {};
    }
    subscribers.add(sub);
    reflectSubCount();
    schedule();
    return function unsubscribe() {
      subscribers.delete(sub);
      reflectSubCount();
    };
  }

  // The pre-load stub's subscribe() returns a closure that splices its own
  // queue array. Once this coordinator adopts that queue (see pending.forEach
  // below), the stub's returned closure has nothing left to splice, so it
  // forwards here instead. Exposed on the public API so the stub can reach it
  // across the two independent script tags that load this file.
  function unsubscribeAdopted(sub) {
    subscribers.delete(sub);
    reflectSubCount();
  }

  function invalidate() {
    docDirty = true;
    schedule();
  }

  // The ResizeObserver on documentElement fires continuously while images and
  // lazy sections land during load, and scheduling a frame per callback would
  // run a ~60fps loop mostly recomputing an unchanged value on hardware that
  // is already saturated. Marking dirty stays immediate and synchronous;
  // only the frame it schedules is debounced, with a trailing call so the
  // last resize in a burst is the one that actually measures.
  const RESIZE_OBSERVER_DEBOUNCE_MS = 150;
  let resizeObserverTimer = null;

  function invalidateFromResizeObserver() {
    docDirty = true;
    if (resizeObserverTimer) {
      clearTimeout(resizeObserverTimer);
    }
    resizeObserverTimer = setTimeout(function () {
      resizeObserverTimer = null;
      schedule();
    }, RESIZE_OBSERVER_DEBOUNCE_MS);
  }

  window.CoraScroll = {
    __installed: true,
    subscribe: subscribe,
    __unsubscribe: unsubscribeAdopted,
    // Anything that changes document height outside a resize (a drawer opening,
    // a section injected) can tell the coordinator its cached height is stale.
    invalidate: invalidate,
    stats: function () {
      const names = [];
      subscribers.forEach((sub) => names.push(sub.name || '(anonymous)'));
      const result = { frames: frames, subscribers: subscribers.size, names: names };
      if (DEBUG) {
        const rows = [];
        timings.forEach((t, name) => {
          rows.push({ name: name, read: t.read, write: t.write, total: t.read + t.write });
        });
        rows.sort((a, b) => b.total - a.total);
        result.timings = rows;
      }
      return result;
    },
  };

  if (typeof stopFallback === 'function') {
    stopFallback();
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', invalidate, { passive: true });

  // Safari and Firefox can restore a page from the back-forward cache without
  // re-running any script, so the cached scrollHeight from before navigation
  // away is stale the instant the page becomes visible again.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      invalidate();
    }
  });

  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(invalidateFromResizeObserver).observe(document.documentElement);
  }

  // A pending entry that was unsubscribed before this file loaded is marked
  // __cancelled by the stub (see snippets/scroll-coordinator-stub.liquid) and
  // must not be brought back to life here.
  pending.forEach((sub) => {
    if (!sub.__cancelled) {
      subscribe(sub);
    }
  });
  schedule();
})();
